/**
 * @file 公用方法
 */
;('use strict')

import { pages as mainPages, subPackages, globalStyle } from '../pages.json'
import { resolveI18nNavigationBarTitle } from '@/locale/navigation-bar'
import {
  awaitH5LocationNavLoadingPaint,
  scheduleH5LocationNavLoadingFallbackHide,
  showH5LocationNavLoading,
} from '@/utils/h5-location-nav-loading'
import {
  markCloudWebPaymentExternalNavPending,
  runWhenUpsdkReady,
} from '@/utils/union-pay-upsdk-ready'

/**
 * 数据类型判断
 * @param {*} data 需要判断的数据
 * @return {string} 数据类型
 */
export const getType = (data) => {
  return Object.prototype.toString.call(data).split(' ')[1].slice(0, -1).toLowerCase()
}

/**
 * 数据判空
 * object 继承属性不计入判断
 * @param {*} data 需要判断的数据
 * @return {boolean} 判断结果
 */
export const isEmpty = (data) => {
  const dataType = getType(data) // 数据类型

  switch (dataType) {
    case 'undefined':
      return true
    case 'null':
      return true
    case 'number':
      return data === 0
    case 'string':
      return data === ''
    case 'boolean':
      return data
    case 'symbol':
      return false
    case 'function':
      return false
    case 'array':
      return data.length === 0
    case 'object':
      for (let key in data) {
        if (data.hasOwnProperty(key)) {
          return false
        }
      }
      return true
    default:
      throw new Error('isEmpty: cannot judge this data')
  }
}

/**
 * 数据深拷贝
 * object 只能拷贝可枚举属性
 * @param {*} data 需拷贝的数据
 * @return {*} 拷贝结构
 */
export const deepClone = (data) => {
  const dataType = getType(data) // 数据类型

  switch (dataType) {
    case 'array':
      return data.map((el) => deepClone(el))
    case 'object':
      const newObj = {}
      for (let key in data) {
        newObj[key] = deepClone(data[key])
      }
      return newObj
    case 'function':
      const newFn = function (...params) {
        data.apply(this, params)
      }
      return newFn
    default:
      return data
  }
}

/**
 * 数据格式校验
 * @param {number | string} value 需要校验的数据
 * @param {string} type 需要判断的类型
 * @return {boolean} 是否满足格式
 */
export const testFormat = (value, type) => {
  const dataType = getType(value) // 数据类型
  let regexp = /(.*)?/

  if (dataType !== 'number' && dataType !== 'string') {
    throw new Error('testFormat: data is invalid')
  }

  switch (type) {
    // 手机
    case 'phone':
      regexp = /^(\+86)?1[0-9]{10}$/
      break
    // 座机
    case 'tel':
      regexp = /^((\d{10,12})|(\d{7,8})|(\d{3}-\d{8})|(\d{4}-\d{8})|(\d{4}-\d{7}))$/
      break
    // 数字
    case 'number':
      regexp = /^(\+|-)?(([1-9]\d*)|(0{1}))(\.\d+)?$/
      break
    // 整数
    case 'integer':
      regexp = /^(\+|-)?[0-9]\d*$/
      break
    // 一位小数
    case 'decimal1':
      regexp = /^(([1-9]{1}\d*)|(0{1}))(\.\d{1})$/
      break
    // 两位小数
    case 'decimal2':
      regexp = /^(([1-9]{1}\d*)|(0{1}))(\.\d{2})$/
      break
    // 网址
    case 'url':
      regexp =
        /^(?:(http|https|ftp):\/\/)((?:[\w-]+\.)+[a-zA-Z0-9]+)((?:\/[^/?#]*)+)?(\?[^#]+)?(#.+)?$/
      break
    // 邮箱
    case 'email':
      regexp = /^[a-z0-9A-Z]+[-|a-z0-9A-Z._]+@([a-z0-9A-Z]+(-[a-z0-9A-Z]+)?\.)+[a-z]{2,}$/
      break
    // 身份证号(含15位和18位)
    case 'idCard':
      regexp =
        /(^([1-6][1-9]|50)\d{4}(18|19|20)\d{2}((0[1-9])|10|11|12)(([0-2][1-9])|10|20|30|31)\d{3}[0-9Xx]$)|(^([1-6][1-9]|50)\d{4}\d{2}((0[1-9])|10|11|12)(([0-2][1-9])|10|20|30|31)\d{3}$)/
      break
    default:
      throw new Error('testFormat: type error')
  }

  return regexp.test(value)
}

/**
 * underscore 节流函数，返回函数连续调用时，func 执行频率限定为 次 / wait
 *
 * @param  {function}   func      回调函数
 * @param  {number}     wait      表示时间窗口的间隔
 * @param  {object}     options   如果想忽略开始函数的的调用，传入{leading: false}。
 *                                如果想忽略结尾函数的调用，传入{trailing: false}
 *                                两者不能共存，否则函数不能执行
 * @return {function}             返回客户调用函数
 */
export const throttle = function (func, wait, options) {
  let context, args, result
  let timeout = null
  // 之前的时间戳
  let previous = 0
  // 如果 options 没传则设为空对象
  if (!options) options = {}
  // 定时器回调函数
  const later = function () {
    // 如果设置了 leading，就将 previous 设为 0
    // 用于下面函数的第一个 if 判断
    previous = options.leading === false ? 0 : +new Date()
    // 置空一是为了防止内存泄漏，二是为了下面的定时器判断
    timeout = null
    result = func.apply(context, args)
    if (!timeout) context = args = null
  }
  return function () {
    // 获得当前时间戳
    const now = +new Date()
    // 首次进入前者肯定为 true
    // 如果需要第一次不执行函数
    // 就将上次时间戳设为当前的
    // 这样在接下来计算 remaining 的值时会大于0
    if (!previous && options.leading === false) previous = now
    // 计算剩余时间
    const remaining = wait - (now - previous)
    context = this
    args = arguments
    // 如果当前调用已经大于上次调用时间 + wait
    // 或者用户手动调了时间
    // 如果设置了 trailing，只会进入这个条件
    // 如果没有设置 leading，那么第一次会进入这个条件
    // 还有一点，您可能会觉得开启了定时器那么应该不会进入这个 if 条件了
    // 其实还是会进入的，因为定时器的延时
    // 并不是准确的时间，很可能您设置了2秒
    // 但是他需要2.2秒才触发，这时候就会进入这个条件
    if (remaining <= 0 || remaining > wait) {
      // 如果存在定时器就清理掉否则会调用二次回调
      if (timeout) {
        clearTimeout(timeout)
        timeout = null
      }
      previous = now
      result = func.apply(context, args)
      if (!timeout) context = args = null
    } else if (!timeout && options.trailing !== false) {
      // 判断是否设置了定时器和 trailing
      // 没有的话就开启一个定时器
      // 并且不能不能同时设置 leading 和 trailing
      timeout = setTimeout(later, remaining)
    }
    return result
  }
}

/**
 * 防抖函数，返回函数连续调用时，空闲时间必须大于或等于 wait，func 才会执行
 *
 * @param  {function} func        回调函数
 * @param  {number}   wait        表示时间窗口的间隔
 * @param  {boolean}  immediate   设置为ture时，立即调用函数
 * @return {function}             返回客户调用函数
 */
export const debounce = function (func, wait = 50, immediate = true) {
  let timer, context, args

  // 延迟执行函数
  const later = () =>
    setTimeout(() => {
      // 延迟函数执行完毕，清空缓存的定时器序号
      timer = null
      // 延迟执行的情况下，函数会在延迟函数中执行
      // 使用到之前缓存的参数和上下文
      if (!immediate) {
        func.apply(context, args)
        context = args = null
      }
    }, wait)

  // 这里返回的函数是每次实际调用的函数
  return function (...params) {
    // 如果没有创建延迟执行函数（later），就创建一个
    if (!timer) {
      timer = later()
      // 如果是立即执行，调用函数
      // 否则缓存参数和调用上下文
      if (immediate) {
        func.apply(this, params)
      } else {
        context = this
        args = params
      }
      // 如果已有延迟执行函数（later），调用的时候清除原来的并重新设定一个
      // 这样做延迟函数会重新计时
    } else {
      clearTimeout(timer)
      timer = later()
    }
  }
}

/**
 * 按指定格式输出时间
 * @param {Object} time   时间对象
 * @param {string} format 自定义格式字符串, 均为可选, 不传则使用默认格式
 *     yyyy/yy: 年, MM: 月, dd: 日, HH: 时, mm: 分, ss: 秒
 * @return {string} 格式化结果
 */
export const formatTime = (time, format) => {
  let result = format

  // 数据校验
  if (isEmpty(time)) {
    throw new Error('formatTime: time is invalid')
  } else if ((getType(result) !== 'string' && getType(result) !== 'undefined') || result === '') {
    throw new Error('formatTime: format is invalid')
  } else if (
    getType(result) !== 'string' &&
    getType(result) !== 'undefined' &&
    getType(result) !== 'null'
  ) {
    throw new Error('getTime: param is invalid')
  }

  // 未指定格式，返回默认格式
  if (!result) {
    return `${time.year}-${time.month}-${time.day} ${time.hour}:${time.minites}:${time.seconds}`
  }

  if (time.year && result.indexOf('yyyy') >= 0) {
    result = result.replace('yyyy', time.year)
  } else if (time.year && result.indexOf('yy') >= 0) {
    result = result.replace('yy', time.year.toString().slice(-2))
  }
  time.month && (result = result.replace('MM', time.month))
  time.day && (result = result.replace('dd', time.day))
  time.hour && (result = result.replace('HH', time.hour))
  time.minites && (result = result.replace('mm', time.minites))
  time.seconds && (result = result.replace('ss', time.seconds))
  time.times && (result = result.replace('SSS', time.times))

  return result
}

/**
 * 获取当前时间
 * @param {boolean} isFormat 是否格式化时间
 * @param {string=} format   自定义格式字符串, 均为可选, 不传则使用默认格式
 *     yyyy/yy: 年, MM: 月, dd: 日, HH: 时, mm: 分, ss: 秒
 * @return {string|Object} 已格式或未格式的时间
 */
export const getTime = (isFormat = true, format) => {
  const date = new Date()
  const currentTime = {
    year: date.getFullYear(),
    month: (date.getMonth() + 1).toString().padStart(2, '0'),
    day: date.getDate().toString().padStart(2, '0'),
    hour: date.getHours().toString().padStart(2, '0'),
    minites: date.getMinutes().toString().padStart(2, '0'),
    seconds: date.getSeconds().toString().padStart(2, '0'),
    times: (date.getTime() % 60).toString().padStart(3, '0'),
  }

  if (isFormat) {
    return formatTime(currentTime, format)
  }
  return currentTime
}

/**
 * 时间偏移
 * @param {number}   offset    偏移量, 单位秒, 正数向后，负数向前
 * @param {string=}  startTime 起始时间, yyyy-MM-dd HH:mm:ss 格式或 yyyy/MM/dd HH:mm:ss 格式
 * @param {boolean=} isFormat  是否格式化时间, 默认格式化
 * @param {string=}  format    自定义格式字符串, 均为可选, 不传则使用默认格式
 *     yyyy/yy: 年, MM: 月, dd: 日, HH: 时, mm: 分, ss: 秒
 * @return {string|Object} 已格式或未格式的时间
 */
export const offsetTime = ({ offset, startTime = getTime(), isFormat = true, format }) => {
  const timeReg = /^[0-9]{4}((-|\/)[0-9]{2}){2}\s[0-9]{2}(:[0-9]{2}){2}$/
  // 起始时间格式
  if (!timeReg.test(startTime)) {
    throw new Error('offsetTime: startTime is invalid')
  }
  // 偏移格式
  if (!testFormat(offset, 'integer')) {
    throw new Error('offsetTime: offset is invalid')
  }

  const start = new Date(startTime.replaceAll('-', '/')).getTime() // 起始时间戳
  const resultTime = offset * 1000 + start // 偏移后的时间戳
  const thatDay = new Date(resultTime) // 目标时间对象
  const result = {
    year: thatDay.getFullYear(),
    month: (thatDay.getMonth() + 1).toString().padStart(2, '0'),
    day: thatDay.getDate().toString().padStart(2, '0'),
    hour: thatDay.getHours().toString().padStart(2, '0'),
    minites: thatDay.getMinutes().toString().padStart(2, '0'),
    seconds: thatDay.getSeconds().toString().padStart(2, '0'),
    times: (thatDay.getTime() % 60).toString().padStart(3, '0'),
  }

  if (isFormat) {
    return formatTime(result, format)
  }
  return result
}

/**
 * 获取目标时间到当前时间的偏移
 * @desc 一年365天, 一月30天, 一天24小时
 * @param {string} time    目标时间
 * @param {Array}  typeArr 格式数组, year-年, month-月, day-日, hour-时, minute-分, second-秒
 * @return {Object} 偏移数据对象
 */
export const getTimeOffset = (time, typeArr = ['day']) => {
  const timeReg = /^[0-9]{4}((-|\/)[0-9]{2}){2}\s[0-9]{2}(:[0-9]{2}){2}$/
  // 数据校验
  if (!timeReg.test(time)) throw new Error('getTimeOffset: time is invalid')
  if (getType(typeArr) !== 'array' || !typeArr.length)
    throw new Error('getTimeOffset: typeArr is invalid')

  const targetTime = new Date(time.replaceAll('-', '/')).getTime()
  const curTime = +new Date()
  let timeDistance = Math.abs(curTime - targetTime)

  let result = ''
  if (typeArr.includes('year')) {
    let yearTime = 365 * 24 * 3600 * 1000
    result += `${~~(timeDistance / yearTime)}年`
    timeDistance = timeDistance % yearTime
  }
  if (typeArr.includes('month')) {
    let monthTime = 30 * 24 * 3600 * 1000
    result += `${~~(timeDistance / monthTime)}月`
    timeDistance = timeDistance % monthTime
  }
  if (typeArr.includes('day')) {
    let dayTime = 24 * 3600 * 1000
    result += `${~~(timeDistance / dayTime)}天`
    timeDistance = timeDistance % dayTime
  }
  if (typeArr.includes('hour')) {
    let hourTime = 3600 * 1000
    result += `${~~(timeDistance / hourTime)}小时`
    timeDistance = timeDistance % hourTime
  }
  if (typeArr.includes('minute')) {
    let minuteTime = 3600 * 1000
    result += `${~~(timeDistance / minuteTime)}分钟`
    timeDistance = timeDistance % minuteTime
  }
  if (typeArr.includes('second')) {
    let secondTime = 1000
    result += `${~~(timeDistance / secondTime)}秒`
    timeDistance = timeDistance % secondTime
  }
  result += curTime >= targetTime ? '前' : '后'
  return result
}

/**
 * 数字乘法, 解决 JavaScript 精度问题
 * @param {number} num1 乘数一
 * @param {number} num2 乘数二
 * @return {number} 两数相乘结果
 */
export const numMul = (num1, num2) => {
  if (getType(num1) !== 'number' || getType(num2) !== 'number') {
    throw new Error('numMul: multiplier is invalid')
  }

  const floatLen1 = num1.toString().split('.')[1]?.length || 0 // 乘数一小数长度
  const floatLen2 = num2.toString().split('.')[1]?.length || 0 // 乘数二小数长度

  // 将小数扩大为整数
  const intNum1 = Number(num1.toString().replace('.', ''))
  const intNum2 = Number(num2.toString().replace('.', ''))

  return (intNum1 * intNum2) / Math.pow(10, floatLen1 + floatLen2)
}

/**
 * 数字除法, 解决 JavaScript 精度问题
 * @param {number} num1 被除数
 * @param {number} num2 除数
 * @return {number} 两数相除结果
 */
export const numDiv = (num1, num2) => {
  if (getType(num1) !== 'number' || getType(num2) !== 'number') {
    throw new Error('numDiv: param is invalid')
  }

  const floatLen1 = num1.toString().split('.')[1]?.length || 0 // 被除数小数长度
  const floatLen2 = num2.toString().split('.')[1]?.length || 0 // 除数小数长度

  // 将小数扩大为整数
  const intNum1 = Number(num1.toString().replace('.', ''))
  const intNum2 = Number(num2.toString().replace('.', ''))

  return numMul(intNum1 / intNum2, Math.pow(10, floatLen2 - floatLen1))
}

/**
 * 数字加法, 解决 JavaScript 精度问题
 * @param {number} num1 加数一
 * @param {number} num2 加数二
 * @return {number} 两数相加结果
 */
export const numAdd = (num1, num2) => {
  if (getType(num1) !== 'number' || getType(num2) !== 'number') {
    throw new Error('numAdd: params is invalid')
  }

  const floatLen1 = num1.toString().split('.')[1]?.length || 0 // 加数一小数长度
  const floatLen2 = num2.toString().split('.')[1]?.length || 0 // 加数二小数长度
  const multiplier = Math.pow(10, Math.max(floatLen1, floatLen2)) // 扩展倍数

  return (numMul(num1, multiplier) + numMul(num2, multiplier)) / multiplier
}

/**
 * 数字减法, 解决 JavaScript 精度问题
 * @param {number} num1 被减数
 * @param {number} num2 减数
 * @return {number} 两数相减结果
 */
export const numSub = (num1, num2) => {
  if (getType(num1) !== 'number' || getType(num2) !== 'number') {
    throw new Error('numSub: params is invalid')
  }

  const floatLen1 = num1.toString().split('.')[1]?.length || 0 // 被减数小数长度
  const floatLen2 = num2.toString().split('.')[1]?.length || 0 // 减数小数长度
  const multiplier = Math.pow(10, Math.max(floatLen1, floatLen2)) // 扩展倍数

  return (numMul(num1, multiplier) - numMul(num2, multiplier)) / multiplier
}

// 判断当前H5环境是不是云闪付小程序
export const isInUpApp = () => {
  var agent = navigator.userAgent.toLowerCase().replace(/\s*/g, '')
  var isInsideWallet =
    new RegExp(/(com.unionpay.chsp)/).test(agent) ||
    new RegExp(/(com.unionpay.mobilepay)/).test(agent)
  if (isInsideWallet) {
    // 云闪付环境
    var bankCode = ''
    const reg = /unionpaysdk[0-9]*/gi
    let str = ''
    agent.replace(reg, function (e) {
      str = e
    })
    bankCode = str.slice(11)
    var isbank = new RegExp(/unionpaysdk/).test(agent)
    if (isbank) {
      // 网络支付平台sdk中
      return bankCode
    } else {
      return 'cloud'
    }
  } else {
    return 'h5'
  }
}

function normalizeRoutePath(fullPath) {
  let pathOnly = (fullPath || '').split('?')[0]
  const publicBase = import.meta.env.VITE_APP_PUBLIC_BASE || '/'
  if (publicBase && publicBase !== '/' && pathOnly.startsWith(publicBase)) {
    pathOnly = pathOnly.slice(publicBase.length)
  }
  return pathOnly.replace(/^\/+/, '').replace(/\/+$/, '')
}

/** 根据路由路径读取 pages.json 中配置的导航栏标题 */
export function getNavigationBarTitleByPath(fullPath) {
  const i18nTitle = resolveI18nNavigationBarTitle(fullPath)
  if (i18nTitle) {
    return i18nTitle
  }

  const normalizedPath = normalizeRoutePath(fullPath)
  if (!normalizedPath) {
    return globalStyle?.navigationBarTitleText || ''
  }

  for (const page of mainPages || []) {
    if (page.path === normalizedPath) {
      return page.style?.navigationBarTitleText || globalStyle?.navigationBarTitleText || ''
    }
  }

  for (const subPackage of subPackages || []) {
    const rootPrefix = `${subPackage.root}/`
    if (normalizedPath === subPackage.root || normalizedPath.startsWith(rootPrefix)) {
      const relativePath =
        normalizedPath === subPackage.root
          ? 'index'
          : normalizedPath.slice(subPackage.root.length + 1)
      for (const page of subPackage.pages || []) {
        if (page.path === relativePath) {
          return page.style?.navigationBarTitleText || globalStyle?.navigationBarTitleText || ''
        }
      }
    }
  }

  return globalStyle?.navigationBarTitleText || ''
}

/** 是否从 0息游（source=wujinbao）外跳进入 */
export function isWujinbaoEntrySource(options) {
  const raw = options?.source ?? uni.getStorageSync('entrySource') ?? ''
  if (!raw) {
    return false
  }
  try {
    return decodeURIComponent(String(raw)).trim() === 'wujinbao'
  }
  catch {
    return String(raw).trim() === 'wujinbao'
  }
}

/** 云闪付小程序导航内容行高（px），不含状态栏 */
export const UNION_PAY_NAV_BAR_ROW_PX = 44

/** 0息游 H5 首页（外跳回跳 replace，避免历史栈回到 travel 页） */
export const WUJINBAO_H5_INDEX_URL = 'https://jileyou.wujinbao.net/pages/index/index'

/**
 * 跳转 jileyou.wujinbao.net 时追加 _t 时间戳防缓存；无 query 用 ?，已有 query 用 &。
 * @param {string} url
 * @returns {string}
 */
export function withJileyouWujinbaoTimestamp(url) {
  const target = String(url || '').trim()
  if (!target.includes('jileyou.wujinbao.net')) {
    return target
  }
  const separator = target.includes('?') ? '&' : '?'
  return `${target}${separator}_t=${Date.now()}`
}

/**
 * H5 整页跳转统一入口：外跳前展示全屏 loading，再 location.href / replace。
 * 0息游外跳回跳 jileyou.wujinbao.net 时须走本函数，勿直接写 window.location.*。
 * @param {string} url
 * @param {{ mode?: 'assign' | 'replace', markCloudWebPaymentReturnPending?: boolean }} [options]
 */
export function navigateH5Location(url, options = {}) {
  // #ifdef H5
  if (typeof window === 'undefined') {
    console.warn('[pay/trace]', 'navigateH5Location:skip', 'no-window')
    return
  }
  const targetUrl = String(url ?? '').trim()
  if (targetUrl === '') {
    console.warn('[pay/trace]', 'navigateH5Location:skip', 'empty-url')
    return
  }

  const mode = options.mode ?? 'assign'
  let urlHost = ''
  try {
    urlHost = new URL(targetUrl).host
  } catch {
    urlHost = 'parse-failed'
  }
  console.log('[pay/trace]', 'navigateH5Location:enter', {
    mode,
    urlHost,
    searchLen: targetUrl.includes('?') ? targetUrl.split('?')[1]?.length ?? 0 : 0,
    markCloudWebPaymentReturnPending: !!options.markCloudWebPaymentReturnPending,
  })

  if (isUnionPayCloudWebEnv() && options.markCloudWebPaymentReturnPending) {
    markCloudWebPaymentExternalNavPending()
  }

  showH5LocationNavLoading()

  return awaitH5LocationNavLoadingPaint().then(() => {
    console.log('[pay/trace]', 'navigateH5Location:assign', { mode, urlHost })
    if (mode === 'replace') {
      window.location.replace(targetUrl)
    }
    else {
      window.location.href = targetUrl
    }
    scheduleH5LocationNavLoadingFallbackHide()
  })
  // #endif
}

/**
 * 回跳 0息游 H5（jileyou.wujinbao.net）：追加防缓存时间戳 + 全屏 loading。
 * @param {string} url
 * @param {{ mode?: 'assign' | 'replace' }} [options]
 */
export function navigateToJileyouWujinbaoH5(url, options = {}) {
  navigateH5Location(withJileyouWujinbaoTimestamp(url), options)
}

/** 云闪付小程序官方 UA：iOS com.unionpay.chsp、Android com.unionpay.mobilepay */
const UNION_PAY_MINI_PROGRAM_UA_PATTERN = /(com\.unionpay\.chsp)|(com\.unionpay\.mobilepay)/

/** 云网银行渠道标识：8 位数字，以 49990 或 49992 开头（UA 中 UnionPaySDK 后缀） */
const UNION_PAY_CLOUD_WEB_BANK_CHANNEL_ID_PATTERN = /^(?:49990|49992)\d{3}$/
const UNION_PAY_SDK_BANK_CHANNEL_UA_PATTERN = /unionpaysdk(\d{8})/i

function readNormalizedUserAgent() {
  if (typeof navigator === 'undefined') {
    return ''
  }
  return navigator.userAgent.toLowerCase().replace(/\s*/g, '')
}

function matchUnionPayMiniProgramUserAgent(agent) {
  return UNION_PAY_MINI_PROGRAM_UA_PATTERN.test(agent)
}

/** 从已归一化 UA 解析云网银行渠道标识；无效或缺失时返回空串 */
function parseUnionPayCloudWebBankChannelId(agent) {
  const match = agent.match(UNION_PAY_SDK_BANK_CHANNEL_UA_PATTERN)
  const channelId = match?.[1] ?? ''
  if (channelId === '') {
    return ''
  }
  return UNION_PAY_CLOUD_WEB_BANK_CHANNEL_ID_PATTERN.test(channelId) ? channelId : ''
}

/**
 * 是否云网银行 SDK WebView（UA：UnionPaySDK + 49990/49992 开头 8 位渠道号）。
 */
export function isUnionPayCloudWebEnv() {
  return parseUnionPayCloudWebBankChannelId(readNormalizedUserAgent()) !== ''
}

/**
 * 是否云闪付小程序 WebView（对齐 0息游：云网优先排除，再看 chsp/mobilepay）。
 * 勿用 isInUpApp()==='cloud' 替代——钱包 UA 含 unionpaysdk 时 isInUpApp 会返回银行码而非 'cloud'。
 */
export function isUnionPayMiniProgramEnv() {
  if (isUnionPayCloudWebEnv()) {
    return false
  }
  return matchUnionPayMiniProgramUserAgent(readNormalizedUserAgent())
}

/** 是否云闪付小程序或云网宿主 */
export function isUnionPayHostEnv() {
  return isUnionPayMiniProgramEnv() || isUnionPayCloudWebEnv()
}

const UNION_PAY_IOS_SCROLL_TOUCH_INLINE_STYLE_KEYS = [
  'touch-action',
  'overflow',
  'overflow-x',
  'overflow-y',
  '-webkit-overflow-scrolling',
  'position',
]

function isUnionPayIosH5Target() {
  if (typeof uni === 'undefined' || typeof document === 'undefined') {
    return false
  }
  if (!isUnionPayHostEnv()) {
    return false
  }
  const platform = (uni.getSystemInfoSync().platform ?? '').toLowerCase()
  return platform === 'ios'
}

function collectUnionPayIosH5ScrollRootElements() {
  const roots = [document.documentElement, document.body]
  const appRoot = document.getElementById('app')
  if (appRoot) {
    roots.push(appRoot)
  }
  document.querySelectorAll('uni-page, uni-page-body, uni-page-wrapper').forEach((node) => {
    if (node instanceof HTMLElement) {
      roots.push(node)
    }
  })
  return roots
}

function applyUnionPayIosH5ScrollTouchStyleReset() {
  for (const element of collectUnionPayIosH5ScrollRootElements()) {
    for (const key of UNION_PAY_IOS_SCROLL_TOUCH_INLINE_STYLE_KEYS) {
      element.style.removeProperty(key)
    }
    element.style.touchAction = 'auto'
  }
}

function readUnionPayIosStackTopRoute() {
  const stackPages = getCurrentPages()
  if (!stackPages.length) {
    return ''
  }
  const topPage = stackPages[stackPages.length - 1]
  return String(topPage?.route ?? '').replace(/^\//, '')
}

/** 页级 onPageScroll 详情：window nudge 无效，须 uni.pageScrollTo 唤醒竖滑 */
const UNION_PAY_IOS_PAGE_SCROLL_GESTURE_WAKE_ROUTE_SUFFIXES = [
  'tourism/tourism_detail/tourism_detail',
  'hotel/hotel_detail/hotel_detail',
  'plane/plane_detail/plane_detail',
  'pageB/international_plane/international_plane_detail',
]

function shouldGestureWakeUnionPayIosPageScroll(route) {
  if (!route) {
    return false
  }
  return UNION_PAY_IOS_PAGE_SCROLL_GESTURE_WAKE_ROUTE_SUFFIXES.some(
    suffix => route === suffix || route.endsWith(suffix) || route.includes(suffix),
  )
}

function nudgeUnionPayIosH5ScrollContainers() {
  if (typeof window === 'undefined') {
    return
  }
  const scrollTop = window.scrollY || document.documentElement.scrollTop || 0
  window.scrollTo(0, scrollTop)
  document.querySelectorAll(
    'uni-page-body, .xwjb-viewport-lock-scroll, .detail-page.is-up-app, .detail-page',
  ).forEach((node) => {
    if (node instanceof HTMLElement) {
      node.scrollTop = node.scrollTop
    }
  })
}

/** 云闪付 iOS 页级滚动：短距 pageScrollTo 打断 WKWebView 手势锁死 */
function nudgeUnionPayIosH5PageScrollGestureWake() {
  if (typeof window === 'undefined' || typeof uni === 'undefined') {
    return
  }
  const baselineY = Math.max(
    0,
    window.scrollY || document.documentElement.scrollTop || 0,
  )
  const wakeDeltaPx = 24
  try {
    uni.pageScrollTo({ scrollTop: baselineY + wakeDeltaPx, duration: 0 })
    setTimeout(() => {
      try {
        uni.pageScrollTo({ scrollTop: baselineY, duration: 0 })
      }
      catch {
        /* 部分栈顶态 pageScrollTo 不可用 */
      }
    }, 20)
  }
  catch {
    /* uni.pageScrollTo 在部分栈顶态不可用 */
  }
  window.scrollTo(0, baselineY)
}

function runHostOverlayScrollRecover(_caller) {
  applyUnionPayIosH5ScrollTouchStyleReset()
  nudgeUnionPayIosH5ScrollContainers()
  const stackRoute = readUnionPayIosStackTopRoute()
  if (shouldGestureWakeUnionPayIosPageScroll(stackRoute)) {
    nudgeUnionPayIosH5PageScrollGestureWake()
  }
}

/**
 * 云闪付 iOS H5：关闭 Wot lock-scroll 底部弹层后恢复宿主页竖滑（对齐订单详情 recoverIosUpAppPageScroll，并清理 body overflow）。
 * @param {string} [caller] 调用方标识，便于排查
 */
export function recoverIosUnionPayHostPageScrollAfterOverlayClose(caller = 'host-overlay') {
  if (!isUnionPayIosH5Target()) {
    return
  }
  document.body.style.removeProperty('overflow')
  document.documentElement.style.removeProperty('overflow')
  const runRecover = () => {
    runHostOverlayScrollRecover(caller)
  }
  runRecover()
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => {
      runRecover()
      requestAnimationFrame(runRecover)
    })
  }
  for (const delayMs of [0, 120, 320, 480]) {
    setTimeout(runRecover, delayMs)
  }
  void caller
}

/**
 * 微信小程序 web-view（UA 含 miniprogram 或 `__wxjs_environment === 'miniprogram'`）。
 * 云闪付/云网 UA 常含 miniprogram，须先排除。
 */
export function isWxMiniProgramWebview() {
  if (isUnionPayHostEnv()) {
    return false
  }
  const agent = readNormalizedUserAgent()
  if (/miniprogram/.test(agent)) {
    return true
  }
  if (typeof window !== 'undefined') {
    return window.__wxjs_environment === 'miniprogram'
  }
  return false
}

/** 宿主原生导航承载时隐藏 H5 自定义顶栏（云闪付/云网、微信小程序 web-view） */
export function shouldHideHostedCustomNav() {
  return isUnionPayHostEnv() || isWxMiniProgramWebview()
}

/** 请求头 terminal：云闪付小程序 / 云网 cloud · 微信小程序 web-view applet · 其余 h5 */
export function getRequestTerminalHeader() {
  if (isUnionPayHostEnv()) {
    return 'cloud'
  }
  if (isWxMiniProgramWebview()) {
    return 'applet'
  }
  return 'h5'
}

/** 0息游外跳 + 云闪付/云网宿主 */
export function isWujinbaoUnionPayHostEntry(options) {
  return isWujinbaoEntrySource(options) && isUnionPayHostEnv()
}

/**
 * upsdk 就绪后执行任务：云网走 config+ready；云闪付小程序仍走 pluginReady（行为与改前一致）。
 * @param {() => void | Promise<void>} task
 * @param {{ apiName?: string }} [options]
 */
export function runWhenUnionPayHostUpsdkReady(task, options) {
  if (isUnionPayCloudWebEnv()) {
    return runWhenUpsdkReady(task, options).catch(() => {
      /* config 成败日志在 union-pay-upsdk-ready 内统一输出 */
    })
  }

  if (typeof upsdk === 'undefined') {
    task()
    return
  }

  if (isUnionPayMiniProgramEnv() && upsdk.pluginReady) {
    upsdk.pluginReady(task)
    return
  }

  task()
}

/**
 * 云网聚分期/中金：去掉跳转链接末尾 &spec_src=scanQrcode。
 * @param {unknown} rawData
 * @returns {string}
 */
export function resolveUpWebInstallmentNavigateLink(rawData) {
  const rawLink = String(rawData ?? '').trim()
  if (!isUnionPayCloudWebEnv()) {
    return rawLink
  }
  const specSrcSuffix = '&spec_src=scanQrcode'
  if (rawLink.endsWith(specSrcSuffix)) {
    return rawLink.slice(0, -specSrcSuffix.length)
  }
  return rawLink
}

const UP_APP_NAV_CHROME_READY_MIN_MS = 320
const UP_APP_VIEWPORT_NAV_BAND_MIN_PX = 72
const UP_APP_VIEWPORT_NAV_BAND_MAX_PX = 100
const UP_APP_VIEWPORT_NAV_BAND_INFLIGHT_TOLERANCE_PX = 12
const UP_APP_NAV_CHROME_READY_DELAYS_MS = [0, 120, 280, 320, 480]

/** scheduleUpAppNavChromeReady 定时器，onShow 重入时须清掉避免误放行 */
let upAppNavChromeReadyTimerIds = []

/** 与 0息游 systemInfo.navigation 对齐 */
const upAppNavigation = {
  statusBarHeight: 0,
  titleBarHeight: UNION_PAY_NAV_BAR_ROW_PX,
  navHeight: 0,
  sideWidth: 23,
  safeAreaWidth: 0,
}

/** 与 0息游 platform.unionPayNavChrome 对齐 */
const upAppNavChrome = {
  nativeBarHidden: false,
  viewportNativeBandPx: 0,
  windowHeightBaseline: 0,
}

/** 云网：沿用银行 App 原生顶栏，不调 upsdk.setTitleStyle；仅重置内存态避免误判 hide */
export function ensureUnionPayCloudWebHostedNativeNavVisible() {
  if (!isUnionPayCloudWebEnv()) {
    return
  }

  upAppNavChrome.nativeBarHidden = false
  upAppNavChrome.viewportNativeBandPx = 0
  upAppNavChrome.windowHeightBaseline = 0
}

/** 首屏 / 刷新：云网保持原生顶栏；云闪付小程序走既有 hide 逻辑（各页 onShow 补刀） */
export function bootstrapUnionPayNavChrome() {
  if (isUnionPayCloudWebEnv()) {
    ensureUnionPayCloudWebHostedNativeNavVisible()
  }
}

function parseUpsdkPx(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

function applyUpAppUpsdkMetrics(sysPayload) {
  const statusBarHeight = parseUpsdkPx(sysPayload?.statusBarHeight)
  const screenWidth = parseUpsdkPx(sysPayload?.screenWidth) || parseUpsdkPx(sysPayload?.windowWidth)
  const fallbackStatusBar = upAppNavigation.statusBarHeight || getUpAppStatusBarHeightPx()
  const effectiveStatusBar = statusBarHeight > 0 ? statusBarHeight : fallbackStatusBar

  if (statusBarHeight > 0) {
    upAppNavigation.statusBarHeight = statusBarHeight
  }
  upAppNavigation.titleBarHeight = UNION_PAY_NAV_BAR_ROW_PX
  upAppNavigation.navHeight = effectiveStatusBar + UNION_PAY_NAV_BAR_ROW_PX
  if (screenWidth > 0) {
    upAppNavigation.safeAreaWidth = screenWidth
  }
}

function pullUpAppNavigationMetricsFromUpsdk() {
  if (!isUnionPayMiniProgramEnv() || typeof upsdk === 'undefined' || !upsdk.getSystemInfo) {
    return
  }
  upsdk.getSystemInfo({
    success: (result) => {
      applyUpAppUpsdkMetrics(result)
    },
  })
}

/**
 * upsdk.pluginReady 后同步状态栏高度（对齐 0息游 refreshUnionPayNavigationLayout）。
 */
export function initUpAppNavigationLayout() {
  if (!isUnionPayMiniProgramEnv() || typeof upsdk === 'undefined') {
    return
  }
  const runPull = () => {
    pullUpAppNavigationMetricsFromUpsdk()
  }
  if (upsdk.pluginReady) {
    upsdk.pluginReady(runPull)
    runPull()
  }
}

export function getUpAppNavChromeState() {
  return upAppNavChrome
}

function resolveUpAppCanonicalNavBandPx(runtimeInfo) {
  const statusBarPx = Number(runtimeInfo?.statusBarHeight ?? 0)
  const effectiveStatusBarPx = statusBarPx > 0 ? statusBarPx : getUpAppStatusBarHeightPx()
  return effectiveStatusBarPx + UNION_PAY_NAV_BAR_ROW_PX
}

function upAppViewportNavBandShrinkMaxPx(canonicalNavBandPx) {
  return Math.max(UP_APP_VIEWPORT_NAV_BAND_MAX_PX, canonicalNavBandPx + 24)
}

/**
 * 归一化宿主顶栏 band：hide 后 windowTop 仍可能读到整段顶栏高度，勿误判 band=0。
 */
function normalizeUpAppViewportNavBandPx(options) {
  const {
    windowTop,
    shrinkPx,
    canonicalNavBandPx,
    nativeBarHidden,
    viewportFullyExpanded,
  } = options

  if (nativeBarHidden && viewportFullyExpanded) {
    if (windowTop >= canonicalNavBandPx - UP_APP_VIEWPORT_NAV_BAND_INFLIGHT_TOLERANCE_PX) {
      return Math.min(windowTop, canonicalNavBandPx)
    }
    return 0
  }

  const shrinkMaxPx = upAppViewportNavBandShrinkMaxPx(canonicalNavBandPx)
  let bandPx = Math.max(0, windowTop)

  if (shrinkPx >= UP_APP_VIEWPORT_NAV_BAND_MIN_PX && shrinkPx <= shrinkMaxPx) {
    bandPx = Math.max(bandPx, shrinkPx)
  }

  if (bandPx <= 0) {
    return 0
  }

  if (bandPx > canonicalNavBandPx + UP_APP_VIEWPORT_NAV_BAND_INFLIGHT_TOLERANCE_PX) {
    bandPx = canonicalNavBandPx
  }

  if (
    nativeBarHidden
    && shrinkPx < UP_APP_VIEWPORT_NAV_BAND_MIN_PX
    && windowTop > canonicalNavBandPx
  ) {
    return 0
  }

  return bandPx
}

function measureUpAppViewportNativeBandPxPhysical() {
  if (!isUnionPayMiniProgramEnv()) {
    return 0
  }

  const runtimeInfo = uni.getSystemInfoSync()
  const windowHeight = runtimeInfo.windowHeight ?? 0
  const windowTop = runtimeInfo.windowTop ?? 0
  const canonicalNavBandPx = resolveUpAppCanonicalNavBandPx(runtimeInfo)
  const baseline = upAppNavChrome.windowHeightBaseline
  const shrinkPx = baseline > 0 && windowHeight > 0 ? Math.max(0, baseline - windowHeight) : 0

  return normalizeUpAppViewportNavBandPx({
    windowTop,
    shrinkPx,
    canonicalNavBandPx,
    nativeBarHidden: false,
    viewportFullyExpanded: shrinkPx < UP_APP_VIEWPORT_NAV_BAND_MIN_PX,
  })
}

/** 纯物理视口推断宿主顶栏是否仍占位（hide 标志可能与真实视口不同步） */
function isUpAppViewportPhysicallyShowingNativeBar() {
  if (!isUnionPayMiniProgramEnv()) {
    return false
  }

  const runtimeInfo = uni.getSystemInfoSync()
  const windowTop = Math.max(0, runtimeInfo.windowTop ?? 0)
  const canonicalNavBandPx = resolveUpAppCanonicalNavBandPx(runtimeInfo)

  if (windowTop >= canonicalNavBandPx - UP_APP_VIEWPORT_NAV_BAND_INFLIGHT_TOLERANCE_PX) {
    return true
  }

  const windowHeight = runtimeInfo.windowHeight ?? 0
  const baseline = upAppNavChrome.windowHeightBaseline
  const shrinkPx = baseline > 0 && windowHeight > 0 ? Math.max(0, baseline - windowHeight) : 0

  if (shrinkPx >= UP_APP_VIEWPORT_NAV_BAND_MIN_PX) {
    return true
  }

  return measureUpAppViewportNativeBandPxPhysical() >= UP_APP_VIEWPORT_NAV_BAND_MIN_PX
}

export function refreshUpAppViewportNavBandMetrics() {
  if (!isUnionPayMiniProgramEnv()) {
    upAppNavChrome.viewportNativeBandPx = 0
    return 0
  }

  const runtimeInfo = uni.getSystemInfoSync()
  const windowHeight = runtimeInfo.windowHeight ?? 0
  const windowTop = runtimeInfo.windowTop ?? 0
  const canonicalNavBandPx = resolveUpAppCanonicalNavBandPx(runtimeInfo)

  if (windowHeight > upAppNavChrome.windowHeightBaseline) {
    upAppNavChrome.windowHeightBaseline = windowHeight
  }

  const baseline = upAppNavChrome.windowHeightBaseline
  const shrinkPx = baseline > 0 && windowHeight > 0 ? Math.max(0, baseline - windowHeight) : 0
  const viewportFullyExpanded = shrinkPx < UP_APP_VIEWPORT_NAV_BAND_MIN_PX

  upAppNavChrome.viewportNativeBandPx = normalizeUpAppViewportNavBandPx({
    windowTop,
    shrinkPx,
    canonicalNavBandPx,
    nativeBarHidden: upAppNavChrome.nativeBarHidden,
    viewportFullyExpanded,
  })
  return upAppNavChrome.viewportNativeBandPx
}

/**
 * H5 是否仍由云闪付宿主原生顶栏占位（hide 已 dispatch 但物理 band 仍在时返回 true）。
 */
export function shouldUpAppUseHostedNativeNavigation() {
  if (!isUnionPayMiniProgramEnv()) {
    return false
  }

  refreshUpAppViewportNavBandMetrics()

  if (!upAppNavChrome.nativeBarHidden) {
    return true
  }

  return isUpAppViewportPhysicallyShowingNativeBar()
}

/**
 * 云闪付沉浸式页是否展示 H5 自定义顶栏（对齐 0息游 shouldShowUnionPayImmersiveCustomNav）。
 * @param {{ pageVisible: boolean, navChromeReady: boolean }} options
 */
export function shouldShowUpAppImmersiveCustomNav(options) {
  if (!isUnionPayMiniProgramEnv()) {
    return true
  }

  if (!options.pageVisible || !options.navChromeReady) {
    return false
  }

  if (upAppNavChrome.nativeBarHidden) {
    return !shouldUpAppUseHostedNativeNavigation()
  }

  return false
}

/**
 * nativeBarHidden 与物理视口不一致时修正标志并可选重试 hide。
 * @param {{ retryHide?: boolean }} [options]
 * @returns {boolean} 是否检测到不一致并已修正
 */
export function reconcileUpAppNavChromeWithPhysicalViewport(options = {}) {
  if (!isUnionPayMiniProgramEnv()) {
    return false
  }

  refreshUpAppViewportNavBandMetrics()

  const runtimeInfo = uni.getSystemInfoSync()
  const windowHeight = runtimeInfo.windowHeight ?? 0
  const baseline = upAppNavChrome.windowHeightBaseline
  const shrinkPx = baseline > 0 && windowHeight > 0 ? Math.max(0, baseline - windowHeight) : 0
  const physicalBandPx = measureUpAppViewportNativeBandPxPhysical()
  const hostLikelyShowingNativeBar =
    shrinkPx >= UP_APP_VIEWPORT_NAV_BAND_MIN_PX
    || physicalBandPx >= UP_APP_VIEWPORT_NAV_BAND_MIN_PX

  if (!upAppNavChrome.nativeBarHidden || !hostLikelyShowingNativeBar) {
    return false
  }

  upAppNavChrome.nativeBarHidden = false
  upAppNavChrome.viewportNativeBandPx = physicalBandPx

  if (options.retryHide !== false) {
    hideUpAppNativeTitleBar()
  }

  scheduleUpAppViewportBandRefresh()
  return true
}

function clearUpAppNavChromeReadyTimers() {
  for (const timerId of upAppNavChromeReadyTimerIds) {
    clearTimeout(timerId)
  }
  upAppNavChromeReadyTimerIds = []
}

function evaluateUpAppNavChromeReady(readySinceMs) {
  const elapsedMs = Date.now() - readySinceMs
  if (elapsedMs < UP_APP_NAV_CHROME_READY_MIN_MS) {
    return false
  }

  refreshUpAppViewportNavBandMetrics()

  if (!upAppNavChrome.nativeBarHidden) {
    return false
  }

  return !shouldUpAppUseHostedNativeNavigation()
}

function scheduleUpAppViewportBandRefresh() {
  if (!isUnionPayMiniProgramEnv()) {
    return
  }
  refreshUpAppViewportNavBandMetrics()
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => {
      refreshUpAppViewportNavBandMetrics()
      setTimeout(() => refreshUpAppViewportNavBandMetrics(), 120)
      setTimeout(() => refreshUpAppViewportNavBandMetrics(), 280)
    })
  }
}

/**
 * 云闪付沉浸式页：hide 门控后再展示 H5 顶栏（对齐 useUnionPayImmersiveNavChrome）。
 * @param {{ pageVisible: () => boolean, onBegin?: () => void, onReady?: () => void, onResync?: () => void }} options
 */
export function scheduleUpAppNavChromeReady(options) {
  if (!isUnionPayMiniProgramEnv()) {
    options.onReady?.()
    return
  }

  clearUpAppNavChromeReadyTimers()
  options.onBegin?.()

  const readySinceMs = Date.now()
  if (options.pageVisible()) {
    hideUpAppNativeTitleBar()
  }

  const tryMarkReady = () => {
    if (!options.pageVisible()) {
      return
    }
    options.onResync?.()
    if (evaluateUpAppNavChromeReady(readySinceMs)) {
      options.onReady?.()
    }
  }

  for (const delayMs of UP_APP_NAV_CHROME_READY_DELAYS_MS) {
    const timerId = setTimeout(() => {
      upAppNavChromeReadyTimerIds = upAppNavChromeReadyTimerIds.filter(id => id !== timerId)
      if (!options.pageVisible()) {
        return
      }
      hideUpAppNativeTitleBar()
      tryMarkReady()
    }, delayMs)
    upAppNavChromeReadyTimerIds.push(timerId)
  }

  const fallbackTimerId = setTimeout(() => {
    upAppNavChromeReadyTimerIds = upAppNavChromeReadyTimerIds.filter(id => id !== fallbackTimerId)
    if (!options.pageVisible()) {
      return
    }
    if (evaluateUpAppNavChromeReady(readySinceMs)) {
      options.onReady?.()
    }
  }, 520)
  upAppNavChromeReadyTimerIds.push(fallbackTimerId)
}

/**
 * 0息游外跳 travel 后整页 unload（手势返回 / replace 回首页）：pagehide 时打标，travel-hub onHide 勿 restore 宿主顶栏。
 */
let upAppSkipShowNativeTitleBarOnPageHide = false

export function bindUpAppWujinbaoBackNavChromeGuardOnce() {
  if (!isUnionPayHostEnv() || typeof window === 'undefined') {
    return
  }
  const globalRef = globalThis
  if (globalRef.__upAppWujinbaoBackNavChromeGuardBound) {
    return
  }
  globalRef.__upAppWujinbaoBackNavChromeGuardBound = true
  window.addEventListener('pagehide', (event) => {
    if (event.persisted || !isWujinbaoEntrySource()) {
      return
    }
    upAppSkipShowNativeTitleBarOnPageHide = true
  })
}

/** travel-hub onHide：若刚由 0息游入口整页离开，跳过 showUpAppNativeTitleBar */
export function consumeUpAppSkipShowNativeTitleBarOnPageHide() {
  if (!upAppSkipShowNativeTitleBarOnPageHide) {
    return false
  }
  upAppSkipShowNativeTitleBarOnPageHide = false
  return true
}

/**
 * 恢复云闪付宿主原生顶栏（仅 travel-hub 隐藏后，其它页须调用）。
 */
export function showUpAppNativeTitleBar() {
  if (!isUnionPayMiniProgramEnv() || typeof upsdk === 'undefined') {
    return
  }

  const runShow = () => {
    if (!upsdk.setTitleStyle) {
      return
    }
    upsdk.setTitleStyle({
      navBackgroundColor: '0xFFFFFFFF',
      backBtnVisible: '1',
      appletTitleBarVisible: '1',
      appletStyle: 'black',
      appletTitleGradientStartColor: '0xFFFFFFFF',
      appletTitleGradientEndColor: '0xFFFFFFFF',
    })
    upAppNavChrome.nativeBarHidden = false
    upAppNavChrome.viewportNativeBandPx = 0
    scheduleUpAppViewportBandRefresh()
  }

  if (upsdk.pluginReady) {
    upsdk.pluginReady(runShow)
    runShow()
  }
}

/**
 * 仅 travel-hub 调用：隐藏云闪付宿主原生顶栏。
 */
export function hideUpAppNativeTitleBar() {
  if (!isUnionPayMiniProgramEnv() || typeof upsdk === 'undefined') {
    return
  }

  const runHide = () => {
    if (!upsdk.setTitleStyle) {
      return
    }
    upsdk.setTitleStyle({
      navBackgroundColor: '0x00FFFFFF',
      backBtnVisible: '0',
      appletTitleBarVisible: '0',
      appletStyle: 'black',
    })
    upAppNavChrome.nativeBarHidden = true
    upAppNavChrome.viewportNativeBandPx = 0
    initUpAppNavigationLayout()
    pullUpAppNavigationMetricsFromUpsdk()
    scheduleUpAppViewportBandRefresh()
  }

  if (upsdk.pluginReady) {
    upsdk.pluginReady(runHide)
    runHide()
  }
}

// #ifdef H5
try {
  if (isUnionPayMiniProgramEnv()) {
    const bootSys = uni.getSystemInfoSync()
    const bootStatusBar = bootSys.statusBarHeight || getUpAppStatusBarFallbackPx(bootSys)
    upAppNavigation.statusBarHeight = bootStatusBar
    upAppNavigation.titleBarHeight = UNION_PAY_NAV_BAR_ROW_PX
    upAppNavigation.navHeight = bootStatusBar + UNION_PAY_NAV_BAR_ROW_PX
    upAppNavChrome.windowHeightBaseline = bootSys.windowHeight ?? 0
  }
}
catch {
  // 启动阶段 systemInfo 不可用则跳过
}
// #endif

/** 云闪付 WebView 隐藏原生顶栏后 uni 常返回 0，按平台兜底（px） */
function getUpAppStatusBarFallbackPx(systemInfo) {
  const platform = String(systemInfo?.platform ?? '').toLowerCase()
  if (platform === 'ios') {
    return 44
  }
  if (platform === 'android') {
    return 24
  }

  // #ifdef H5
  if (typeof navigator !== 'undefined') {
    const agent = navigator.userAgent.toLowerCase()
    if (/iphone|ipad|ipod/.test(agent)) {
      return 44
    }
    if (/android/.test(agent)) {
      return 24
    }
  }
  // #endif

  return 44
}

/** 云闪付 H5 状态栏高度（px）：windowTop / statusBar / safeArea → 平台兜底 */
export function getUpAppStatusBarHeightPx() {
  const systemInfo = uni.getSystemInfoSync()
  const windowTop = systemInfo?.windowTop ?? 0
  if (windowTop > 0) {
    return windowTop
  }

  const screenTop = systemInfo?.screenTop ?? 0
  if (screenTop > 0) {
    return screenTop
  }

  const statusBarHeight = systemInfo?.statusBarHeight ?? 0
  if (statusBarHeight > 0) {
    return statusBarHeight
  }

  const safeAreaTop = systemInfo?.safeArea?.top ?? systemInfo?.safeAreaInsets?.top ?? 0
  if (safeAreaTop > 0) {
    return safeAreaTop
  }

  if (!isUnionPayMiniProgramEnv()) {
    return 0
  }

  return getUpAppStatusBarFallbackPx(systemInfo)
}

/** 读取宿主胶囊几何（云闪付 H5 WebView 可用时与微信同构） */
function readUpAppMenuButtonRect() {
  if (!isUnionPayMiniProgramEnv()) {
    return null
  }

  try {
    const rect = uni.getMenuButtonBoundingClientRect?.()
    if (!rect || rect.top <= 0 || rect.height <= 0) {
      return null
    }

    const topPx = Number(rect.top)
    const heightPx = Number(rect.height)
    const bottomPx = Number(rect.bottom ?? topPx + heightPx)
    const leftPx = Number(rect.left ?? 0)

    if (!Number.isFinite(topPx) || !Number.isFinite(heightPx) || topPx <= 0 || heightPx <= 0) {
      return null
    }

    return {
      top: topPx,
      height: heightPx,
      bottom: Number.isFinite(bottomPx) ? bottomPx : topPx + heightPx,
      left: Number.isFinite(leftPx) ? leftPx : 0,
    }
  }
  catch {
    return null
  }
}

/** 云闪付自定义顶栏右侧避让宽度（px） */
export function getUpAppNavigationCapsuleReservePx(menuButtonRect = readUpAppMenuButtonRect()) {
  const systemInfo = uni.getSystemInfoSync()
  const screenWidth = systemInfo?.screenWidth ?? systemInfo?.windowWidth ?? 0
  if (menuButtonRect && menuButtonRect.left > 0 && screenWidth > 0) {
    return Math.max(94, screenWidth - menuButtonRect.left)
  }
  return 94
}

/**
 * 云闪付自定义顶栏占位（对齐 xwjbNavbarStatusBarPx + getXwjbNavbarBarRowPx）。
 */
export function getUpAppNavChromeMetrics() {
  const statusBarPx = upAppNavigation.statusBarHeight > 0
    ? upAppNavigation.statusBarHeight
    : getUpAppStatusBarHeightPx()
  const navRowPx = UNION_PAY_NAV_BAR_ROW_PX
  return {
    statusBarPx,
    navRowPx,
    navBarTopPx: statusBarPx,
    totalPx: statusBarPx + navRowPx,
    capsuleReservePx: getUpAppNavigationCapsuleReservePx(),
  }
}

/**
 * 同步 + 异步刷新云闪付顶栏占位（hide 原生栏后延迟重读胶囊）。
 * @param {(metrics: ReturnType<typeof getUpAppNavChromeMetrics>) => void} [onResolved]
 */
export function refreshUpAppNavChromeMetrics(onResolved) {
  const applyMetrics = () => {
    const metrics = getUpAppNavChromeMetrics()
    onResolved?.(metrics)
    return metrics
  }

  const metrics = applyMetrics()

  refreshUpAppStatusBarHeightPx(() => {
    applyMetrics()
  })

  if (isUnionPayMiniProgramEnv()) {
    initUpAppNavigationLayout()
    const resyncDelays = [50, 150, 320]
    resyncDelays.forEach((delayMs) => {
      setTimeout(applyMetrics, delayMs)
    })
  }

  return metrics
}

/**
 * upsdk.getSystemInfo 异步回写状态栏高度（pluginReady 后调用）。
 * @param {(heightPx: number) => void} [onResolved]
 */
export function refreshUpAppStatusBarHeightPx(onResolved) {
  const syncHeightPx = getUpAppStatusBarHeightPx()
  if (syncHeightPx > 0) {
    onResolved?.(syncHeightPx)
    return syncHeightPx
  }

  if (!isUnionPayMiniProgramEnv() || typeof upsdk === 'undefined' || !upsdk.pluginReady) {
    onResolved?.(syncHeightPx)
    return syncHeightPx
  }

  const finish = (heightPx) => {
    const resolved = heightPx > 0 ? heightPx : getUpAppStatusBarHeightPx()
    onResolved?.(resolved)
    return resolved
  }

  upsdk.pluginReady(() => {
    if (!upsdk.getSystemInfo) {
      finish(syncHeightPx)
      return
    }
    upsdk.getSystemInfo({
      success: (result) => {
        const parsed = Number(result?.statusBarHeight)
        const upsdkHeightPx = Number.isFinite(parsed) && parsed > 0 ? parsed : 0
        const runtimeHeightPx = getUpAppStatusBarHeightPx()
        const resolvedHeightPx = upsdkHeightPx > 0 ? upsdkHeightPx : runtimeHeightPx
        finish(resolvedHeightPx)
      },
      fail: () => {
        finish(syncHeightPx)
      },
    })
  })

  return syncHeightPx
}

/**
 * 云网：仅 upsdk.setNavigationBarTitle，不调 setTitleStyle。
 * @param {string} [title]
 * @param {(() => void) | undefined} onApplied
 */
function applyUpAppCloudWebNavigationBarTitle(title, onApplied) {
  if (!isUnionPayCloudWebEnv() || typeof upsdk === 'undefined') {
    onApplied?.()
    return
  }

  const runApply = () => {
    const trimmedTitle = String(title ?? '').trim()
    if (trimmedTitle && upsdk.setNavigationBarTitle) {
      upsdk.setNavigationBarTitle({ title: trimmedTitle })
    }
    onApplied?.()
  }

  void runWhenUpsdkReady(runApply, { apiName: 'upsdk.setNavigationBarTitle' }).catch((error) => {
    console.warn('[base] 云网同步导航栏标题失败', error)
    onApplied?.()
  })
}

/**
 * 云闪付小程序：setTitleStyle + 标题；云网：仅 setNavigationBarTitle。
 * @param {string} title 导航栏标题
 * @param {{ wujinbao?: boolean, useHotelGradient?: boolean, onApplied?: () => void }} options
 */
export function applyUpAppNavStyle(title, options = {}) {
  if (!isUnionPayHostEnv() || typeof upsdk === 'undefined') {
    options.onApplied?.()
    return
  }

  if (isUnionPayCloudWebEnv()) {
    applyUpAppCloudWebNavigationBarTitle(title, options.onApplied)
    return
  }

  const isWujinbao = options.wujinbao ?? isWujinbaoEntrySource()
  const useHotelGradient = options.useHotelGradient === true

  const runApply = () => {
    const visibleNativeTitleBar = {
      backBtnVisible: '1',
      appletTitleBarVisible: '1',
    }
    if (isWujinbao || !useHotelGradient) {
      upsdk.setTitleStyle({
        navBackgroundColor: '0xFFFFFFFF',
        appletStyle: 'black',
        appletTitleGradientStartColor: '0xFFFFFFFF',
        appletTitleGradientEndColor: '0xFFFFFFFF',
        ...visibleNativeTitleBar,
      })
    }
    else {
      upsdk.setTitleStyle({
        navBackgroundColor: '0xFF667EEA',
        appletStyle: 'white',
        appletTitleGradientOrient: 'right',
        appletTitleGradientStartColor: '0xFF667EEA',
        appletTitleGradientEndColor: '0xFF764BA2',
        ...visibleNativeTitleBar,
      })
    }
    upAppNavChrome.nativeBarHidden = false
    if (title) {
      upsdk.setNavigationBarTitle({ title })
    }
    options.onApplied?.()
  }

  upsdk.pluginReady(function () {
    runApply()
  })
}

/** 云闪付小程序：白色导航栏（深色标题/返回/状态栏） */
export function setUpAppNavStyleWhite() {
  applyUpAppNavStyle()
}

/** 云闪付小程序：白底样式 + 标题；云网：仅 upsdk 标题 */
export function setUpAppNavTitle(title) {
  applyUpAppNavStyle(title)
}

/** travel-hub 页自行隐藏宿主顶栏，跳过全局白底导航同步 */
const TRAVEL_HUB_PAGE_PATH_FRAGMENT = 'misc/pages/travel-hub'
/** 往返页自行同步「出发 ⇌ 到达」路线标题，跳过静态 i18n「往返」 */
const PLANE_RETURN_PAGE_PATH_FRAGMENT = 'plane/plane_return/plane_return'

/** 按当前路由路径同步云闪付原生导航栏标题 */
export function syncUpAppNavTitleByPath(fullPath) {
  const normalizedPath = String(fullPath ?? '')
  if (normalizedPath.includes(TRAVEL_HUB_PAGE_PATH_FRAGMENT)) {
    return
  }
  if (normalizedPath.includes(PLANE_RETURN_PAGE_PATH_FRAGMENT)) {
    return
  }
  const title = getNavigationBarTitleByPath(fullPath)
  if (title) {
    setUpAppNavTitle(title)
  }
}

/**
 * 获取区域对角线的两点坐标，即这个区域内的最小坐标值和最大坐标值
 *
 * @param pointerArray [[a,b],[c,d]]
 * @return Array {min:number[a,b], max:number[c,d]}
 */
export const getMaxBoundsPointer = (pointerArray) => {
  let lngArray = pointerArray.map((item) => item[0])
  let latArray = pointerArray.map((item) => item[1])

  return {
    min: [Math.min(...lngArray), Math.min(...latArray)],
    max: [Math.max(...lngArray), Math.max(...latArray)],
  }
}
/**
 * 保留数字精度, 以字符串方式处理
 * @param {number} num       需要处理的数据
 * @param {number} precision 精度, 负数表示整数位数
 *     正数: 精确到几位小数, 0: 精确到整数, 负数: 从个位十位向上精确
 * @param {string} method    精确的方式
 *     round: 四舍五入, floor: 向下精确(去尾), ceil: 向上精确(收尾)
 * @return {string} 精确后的结果
 */
export const numFixed = (num, precision, method = 'round') => {
  // 参数校验
  if (getType(num) !== 'number') {
    throw new Error('numFixed: param is not a number')
  } else if (getType(precision) !== 'number' || !testFormat(precision, 'integer')) {
    throw new Error('numFixed: precision is invalid')
  } else if (method !== 'round' && method !== 'floor' && method !== 'ceil') {
    throw new Error('numFixed: method is invalid')
  }

  const number = num.toString()
  const intLen = number.split('.')[0].length // 整数位数
  const floatLen = number.split('.')[1]?.length || 0 // 小数位数
  let result = '' // 存放结果

  // 精度位置在小数部分
  if (precision > 0) {
    // 目标小数长度大于现有小数长度
    if (precision > floatLen) {
      // 操作数是否为整数
      result = floatLen ? number : `${number}.`
      // 直接扩展至目标长度
      result = result.padEnd(intLen + precision + 1, '0')
    }
    // 四舍五入保留精度
    else if (method === 'round') {
      const multiplier = Math.pow(10, precision)

      result = (Math.round(numMul(num, multiplier)) / multiplier).toString()
      // 是否进位变成整数
      result = result.split('.')[1]?.length ? result : `${result}.`
      // 保留至指定精度
      result = result.padEnd(intLen + precision + 1, '0')
    }
    // 去尾保留精度
    else if (method === 'floor') {
      result = number.slice(0, intLen + precision + 1)
    }
    // 收尾保留精度
    else if (method === 'ceil') {
      // 预保留精度
      result = Number(number.slice(0, intLen + precision + 1))
      // 收尾进位
      result = numAdd(result, 1 / Math.pow(10, precision)).toString()
      // 是否进位变成整数
      result = result.split('.')[1]?.length ? result : `${result}.`
      // 保留至指定精度
      result = result.padEnd(intLen + precision + 1, '0')
    }
  }
  // 保留整数
  else if (precision === 0) {
    // 四舍五入保留精度
    if (method === 'round') {
      result = Math.round(num).toString()
    }
    // 去尾保留精度
    else if (method === 'floor') {
      result = Math.floor(num).toString()
    }
    // 收尾保留精度
    else if (method === 'ceil') {
      result = Math.ceil(num).toString()
    }
  }
  // 精度位置在整数部分
  else if (precision <= 0) {
    const precTemp = precision * -1
    // 精度超出整数最高位
    if (precTemp > intLen) {
      result = '0'
    }
    // 四舍五入保留精度
    else if (method === 'round') {
      result = +number.split('.')[0] / Math.pow(10, precTemp)
      result = (Math.round(result) * Math.pow(10, precTemp)).toString()
    }
    // 去尾保留精度
    else if (method === 'floor') {
      result = Math.floor(num)
    }
    // 收尾保留精度
    else if (method === 'ceil') {
      result = Math.ceil(num)
    }
  }

  return result
}

/**
 * 加密字符/数字串
 * @param {(number|string)} val 需要加密值
 */
export const encryptStr = (val) => {
  if (getType(val) !== 'number' && getType(val) !== 'string') {
    throw new Error('encryptStr: param is invalid')
  }

  let str = val.toString()
  return str.length <= 5
    ? str.replace(/(.{1})(.*)(.{1})/, '$1∗∗∗$3')
    : str.replace(/(.{2})(.*)(.{2})/, '$1∗∗∗$3')
}

/**
 * 对象属性过滤
 * @param {Object} obj   需要过滤的对象
 * @param {Array}  attrs 需要保留的属性
 */
export const attrFilter = (obj, attrs) => {
  if (getType(obj) !== 'object') {
    throw new Error('attrFilter: your target object is invalid')
  } else if (getType(attrs) !== 'array') {
    throw new Error('attrFilter: your attrs is not an array')
  }

  return attrs.reduce((total, current) => {
    total[current] = obj[current]
    return total
  }, {})
}

/**
 * 解析链接参数
 * @param   {string} URL 需要解析的链接
 * @returns {Object} 解析得到的参数
 */
export const analyzeURL = (URL) => {
  if (getType(URL) !== 'string') {
    throw new Error('analyzeURL: URL should be a string')
  }

  let start = URL.indexOf('?') + 1
  let resArr =
    start === 0
      ? []
      : URL.substring(start)
          .split('&')
          .map((param) => param.split('='))

  return Object.fromEntries(resArr)
}

/**
 * 时间格式化成字符串
 * 格式 YYYY/yyyy/YY/yy 表示年份
 * MM/M 月份
 * W/w 星期
 * dd/DD/d/D 日期
 * hh/HH/h/H 时间
 * mm/m 分钟
 * ss/SS/s/S 秒
 * @param date
 * @param formatStr
 * @returns {*}
 */
export const dateToStr = (date, formatStr) => {
  var str = formatStr
  var Week = ['日', '一', '二', '三', '四', '五', '六']

  str = str.replace(/yyyy|YYYY/, date.getFullYear())
  str = str.replace(
    /yy|YY/,
    date.getYear() % 100 > 9 ? (date.getYear() % 100).toString() : '0' + (date.getYear() % 100),
  )

  str = str.replace(
    /MM/,
    date.getMonth() + 1 > 9 ? (date.getMonth() + 1).toString() : '0' + (date.getMonth() + 1),
  )
  str = str.replace(/M/g, date.getMonth() + 1)

  str = str.replace(/w|W/g, Week[date.getDay()])

  str = str.replace(/dd|DD/, date.getDate() > 9 ? date.getDate().toString() : '0' + date.getDate())
  str = str.replace(/d|D/g, date.getDate())

  str = str.replace(
    /hh|HH/,
    date.getHours() > 9 ? date.getHours().toString() : '0' + date.getHours(),
  )
  str = str.replace(/h|H/g, date.getHours())
  str = str.replace(
    /mm/,
    date.getMinutes() > 9 ? date.getMinutes().toString() : '0' + date.getMinutes(),
  )
  str = str.replace(/m/g, date.getMinutes())

  str = str.replace(
    /ss|SS/,
    date.getSeconds() > 9 ? date.getSeconds().toString() : '0' + date.getSeconds(),
  )
  str = str.replace(/s|S/g, date.getSeconds())
  return str
}

/**
 * 时间字符串转时间字符串，主要用于后台返回的时间不满足前端显示需要的情况  注意：这里的时间字符串格式必须为 yyyy-MM-dd HH:mm:ss
 * @param {*} dateStr
 * @param {*} formatStr
 */
export const dateStrToDateStr = (dateStr, formatStr = 'YYYY-MM-DD HH:mm:ss') => {
  dateStr = dateStr.replace(/-/g, '/')
  let date = new Date(dateStr)
  return dateToStr(date, formatStr)
}

/**
 * @param {Number} timeStamp 判断时间戳格式是否是毫秒
 * @returns {Boolean}
 */
const isMillisecond = (timeStamp) => {
  const timeStr = String(timeStamp)
  return timeStr.length > 10
}

/**
 * @param {Number} timeStamp 传入的时间戳
 * @param {Number} currentTime 当前时间时间戳
 * @returns {Boolean} 传入的时间戳是否早于当前时间戳
 */
const isEarly = (timeStamp, currentTime) => {
  return timeStamp < currentTime
}

/**
 * @param {String|Number} timeStamp 时间戳
 * @returns {String} 相对时间字符串
 */
export const getRelativeTime = (timeStamp) => {
  // 判断当前传入的时间戳是秒格式还是毫秒
  const IS_MILLISECOND = isMillisecond(timeStamp)
  // 如果是毫秒格式则转为秒格式
  if (IS_MILLISECOND) Math.floor((timeStamp /= 1000))
  // 传入的时间戳可以是数值或字符串类型，这里统一转为数值类型
  timeStamp = Number(timeStamp)
  // 获取当前时间时间戳
  const currentTime = Math.floor(Date.parse(new Date()) / 1000)
  // 判断传入时间戳是否早于当前时间戳
  const IS_EARLY = isEarly(timeStamp, currentTime)
  // 获取两个时间戳差值
  let diff = currentTime - timeStamp
  // 如果IS_EARLY为false则差值取反
  if (!IS_EARLY) diff = -diff
  let resStr = ''
  const dirStr = IS_EARLY ? '前' : '后'
  // 少于等于59秒
  if (diff <= 59) resStr = diff + '秒' + dirStr
  // 多于59秒，少于等于59分钟59秒
  else if (diff > 59 && diff <= 3599) resStr = Math.floor(diff / 60) + '分钟' + dirStr
  // 多于59分钟59秒，少于等于23小时59分钟59秒
  else if (diff > 3599 && diff <= 86399) resStr = Math.floor(diff / 3600) + '小时' + dirStr
  // 多于23小时59分钟59秒，少于等于29天59分钟59秒
  else if (diff > 86399 && diff <= 2623859) resStr = Math.floor(diff / 86400) + '天' + dirStr
  // 多于29天59分钟59秒，少于364天23小时59分钟59秒，且传入的时间戳早于当前
  else if (diff > 2623859 && diff <= 31567859 && IS_EARLY) resStr = getDate(timeStamp)
  else resStr = getDate(timeStamp, 'year')
  return resStr
}

function _parseParams(params) {
  console.log(`params`, params)
  if (!params) {
    return ''
  }
  let str = ''
  for (let key in params) {
    str += `${key}=${params[key]}&`
  }
  console.log(str)
  return str
}

/**
 * 酒店列表最低价是否有效（大于 0 才展示价格）
 * @param {number|string|null|undefined} minPrice
 * @return {boolean}
 */
export function hasHotelMinPrice(minPrice) {
  const parsed = Number(minPrice)
  return Number.isFinite(parsed) && parsed > 0
}

export function pageBack(option) {
  const delta = option.delta || 1
  const success = option.success || (() => {})
  const fail = option.fail || (() => {})
  const path = option.path || option.name

  uni.navigateBack({
    delta,
  })
}

// #ifdef H5
/** 供 union-pay-upsdk-ready 读取；须在 upsdk bootstrap 前完成 */
function syncUnionPayRuntimeGlobals() {
  globalThis.IS_IN_UP_WEB = isUnionPayCloudWebEnv()
  globalThis.IS_IN_UP_APP = isUnionPayMiniProgramEnv()
}
syncUnionPayRuntimeGlobals()
// #endif
